import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Share2, Instagram, Facebook, Twitter, Youtube, Plus, Trash2, Save, X,
  ChevronLeft, ChevronRight, Clock, Send, Eye, Edit3, Search, Image,
  Calendar, Link2, CheckCircle, AlertTriangle, BarChart3, Copy, Globe,
  RefreshCw, Unlink, ExternalLink, FileText, Video, Hash, Megaphone
} from 'lucide-react'
import { socialAccountService, socialPostService } from '../services/AdminServices'
import { useAuth } from '../context/AuthContext'
import './SocialMediaHub.css'

// ── Constants ─────────────────────────────────────────────
const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F', features: ['image', 'video', 'carousel', 'story', 'reel'] },
  { value: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2', features: ['image', 'video', 'text', 'link', 'story'] },
  { value: 'twitter', label: 'X (Twitter)', icon: Twitter, color: '#1DA1F2', features: ['text', 'image', 'video', 'thread'] },
  { value: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000', features: ['video', 'short'] },
  { value: 'tiktok', label: 'TikTok', icon: Video, color: '#010101', features: ['video'] },
  { value: 'linkedin', label: 'LinkedIn', icon: Globe, color: '#0A66C2', features: ['text', 'image', 'video', 'article'] },
]

const POST_STATUSES = [
  { value: 'draft', label: 'Borrador', color: '#64748b' },
  { value: 'scheduled', label: 'Programado', color: '#f59e0b' },
  { value: 'published', label: 'Publicado', color: '#4ade80' },
  { value: 'partial', label: 'Parcial', color: '#fb923c' },
  { value: 'failed', label: 'Error', color: '#ef4444' },
]

const CONTENT_TYPES = [
  { value: 'image', label: 'Imagen', icon: Image },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'text', label: 'Solo texto', icon: FileText },
  { value: 'link', label: 'Link', icon: Link2 },
  { value: 'carousel', label: 'Carrusel', icon: Copy },
]

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}
function formatDateTime(d) {
  if (!d) return '—'
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function getPlatformInfo(platform) {
  return PLATFORMS.find(p => p.value === platform) || { label: platform, color: '#64748b', icon: Globe }
}

function getStatusInfo(status) {
  return POST_STATUSES.find(s => s.value === status) || POST_STATUSES[0]
}

// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════
export default function SocialMediaHub() {
  const { user } = useAuth()
  const [tab, setTab] = useState('calendar') // accounts | calendar | compose | history
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState(null)
  const [processing, setProcessing] = useState(false)

  // ── Accounts state ──
  const [accounts, setAccounts] = useState([])
  const [showAddAccount, setShowAddAccount] = useState(false)
  const [newAccountForm, setNewAccountForm] = useState({ platform: 'instagram', name: '', handle: '', accessToken: '', pageId: '', igBusinessAccountId: '', tiktokOpenId: '' })

  // ── Posts state ──
  const [posts, setPosts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [platformFilter, setPlatformFilter] = useState('all')

  // ── Calendar state ──
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()

  // ── Compose state ──
  const [editingPost, setEditingPost] = useState(null)
  const [composeForm, setComposeForm] = useState({
    content: '',
    platforms: [],
    contentType: 'image',
    mediaUrls: [],
    hashtags: '',
    scheduledAt: '',
    status: 'draft',
    caption: '',
    link: '',
    notes: '',
  })

  // ── Load data ──
  const loadAccounts = useCallback(async () => {
    try {
      const data = await socialAccountService.list()
      setAccounts(data.accounts || [])
    } catch (e) {
      console.error('Error loading social accounts:', e)
    }
  }, [])

  const loadPosts = useCallback(async () => {
    try {
      const params = {}
      if (statusFilter !== 'all') params.status = statusFilter
      if (platformFilter !== 'all') params.platform = platformFilter
      const data = await socialPostService.list(params)
      setPosts(data.posts || [])
    } catch (e) {
      console.error('Error loading social posts:', e)
    }
  }, [statusFilter, platformFilter])

  useEffect(() => {
    Promise.all([loadAccounts(), loadPosts()]).finally(() => setLoading(false))
  }, [loadAccounts, loadPosts])

  const flash = (msg, isError = false) => {
    setMessage({ text: msg, error: isError })
    setTimeout(() => setMessage(null), 4000)
  }

  // ══════════════════════════════════════════════════════════
  // ACCOUNTS TAB
  // ══════════════════════════════════════════════════════════
  const handleAddAccount = async () => {
    if (!newAccountForm.name || !newAccountForm.handle) return
    setProcessing(true)
    try {
      await socialAccountService.create({
        ...newAccountForm,
        connectedAt: Date.now(),
        status: 'active',
      })
      setNewAccountForm({ platform: 'instagram', name: '', handle: '', accessToken: '', pageId: '', igBusinessAccountId: '', tiktokOpenId: '' })
      setShowAddAccount(false)
      await loadAccounts()
      flash('Cuenta conectada exitosamente')
    } catch (e) {
      flash('Error: ' + (e.message || 'No se pudo conectar'), true)
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteAccount = async (id) => {
    if (!confirm('¿Desconectar esta cuenta?')) return
    try {
      await socialAccountService.delete(id)
      await loadAccounts()
      flash('Cuenta desconectada')
    } catch (e) {
      flash('Error al desconectar: ' + e.message, true)
    }
  }

  const handleToggleAccountStatus = async (account) => {
    try {
      const newStatus = account.status === 'active' ? 'paused' : 'active'
      await socialAccountService.update(account.id, { status: newStatus })
      await loadAccounts()
      flash(`Cuenta ${newStatus === 'active' ? 'activada' : 'pausada'}`)
    } catch (e) {
      flash('Error: ' + e.message, true)
    }
  }

  // ══════════════════════════════════════════════════════════
  // COMPOSE / POSTS
  // ══════════════════════════════════════════════════════════
  const resetCompose = () => {
    setComposeForm({
      content: '', platforms: [], contentType: 'image', mediaUrls: [],
      hashtags: '', scheduledAt: '', status: 'draft', caption: '', link: '', notes: '',
    })
    setEditingPost(null)
  }

  const openCompose = (post = null) => {
    if (post) {
      setEditingPost(post.id)
      setComposeForm({
        content: post.content || '',
        platforms: post.platforms || [],
        contentType: post.contentType || 'image',
        mediaUrls: post.mediaUrls || [],
        hashtags: post.hashtags || '',
        scheduledAt: post.scheduledAt || '',
        status: post.status || 'draft',
        caption: post.caption || '',
        link: post.link || '',
        notes: post.notes || '',
      })
    } else {
      resetCompose()
    }
    setTab('compose')
  }

  const openComposeForDate = (dateStr) => {
    resetCompose()
    setComposeForm(prev => ({ ...prev, scheduledAt: dateStr + 'T10:00' }))
    setTab('compose')
  }

  const handleSavePost = async (asDraft = true) => {
    if (!composeForm.content && !composeForm.caption) {
      flash('Escribe algo para publicar', true)
      return
    }
    if (composeForm.platforms.length === 0) {
      flash('Seleccioná al menos una plataforma', true)
      return
    }
    setProcessing(true)
    try {
      const payload = {
        ...composeForm,
        status: asDraft ? 'draft' : (composeForm.scheduledAt ? 'scheduled' : 'draft'),
        createdBy: user?.email || 'admin',
        updatedAt: Date.now(),
      }
      if (editingPost) {
        await socialPostService.update(editingPost, payload)
        flash('Post actualizado')
      } else {
        payload.createdAt = Date.now()
        await socialPostService.create(payload)
        flash(asDraft ? 'Borrador guardado' : 'Post programado')
      }
      resetCompose()
      await loadPosts()
      setTab('calendar')
    } catch (e) {
      flash('Error: ' + (e.message || 'No se pudo guardar'), true)
    } finally {
      setProcessing(false)
    }
  }

  const handlePublishNow = async (postId) => {
    if (!confirm('¿Publicar ahora en todas las plataformas seleccionadas?')) return
    setProcessing(true)
    try {
      await socialPostService.publish(postId)
      await loadPosts()
      flash('Publicación enviada')
    } catch (e) {
      flash('Error al publicar: ' + e.message, true)
    } finally {
      setProcessing(false)
    }
  }

  const handleDeletePost = async (id) => {
    if (!confirm('¿Eliminar este post?')) return
    try {
      await socialPostService.delete(id)
      await loadPosts()
      flash('Post eliminado')
    } catch (e) {
      flash('Error: ' + e.message, true)
    }
  }

  const togglePlatform = (platform) => {
    setComposeForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }))
  }

  // ══════════════════════════════════════════════════════════
  // CALENDAR LOGIC
  // ══════════════════════════════════════════════════════════
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1)
    let startDay = firstDay.getDay() - 1
    if (startDay < 0) startDay = 6
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
    const days = []
    for (let i = 0; i < startDay; i++) days.push(null)
    for (let d = 1; d <= daysInMonth; d++) days.push(d)
    return days
  }, [currentYear, currentMonth])

  const postsByDate = useMemo(() => {
    const map = {}
    posts.forEach(p => {
      if (!p.scheduledAt) return
      const dateKey = new Date(p.scheduledAt).toISOString().slice(0, 10)
      if (!map[dateKey]) map[dateKey] = []
      map[dateKey].push(p)
    })
    return map
  }, [posts])

  // ── Filtered posts for history ──
  const filteredPosts = useMemo(() => {
    let result = posts
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      result = result.filter(p =>
        p.content?.toLowerCase().includes(q) ||
        p.caption?.toLowerCase().includes(q) ||
        p.hashtags?.toLowerCase().includes(q)
      )
    }
    return result.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  }, [posts, searchTerm])

  // ── KPIs ──
  const totalPosts = posts.length
  const draftPosts = posts.filter(p => p.status === 'draft').length
  const scheduledPosts = posts.filter(p => p.status === 'scheduled').length
  const publishedPosts = posts.filter(p => p.status === 'published').length
  const activeAccounts = accounts.filter(a => a.status === 'active').length

  // ══════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="sm-page">
        <div className="sm-loading"><div className="sm-spinner" /> Cargando redes sociales...</div>
      </div>
    )
  }

  return (
    <div className="sm-page">
      {/* Flash message */}
      {message && (
        <div className={`sm-flash ${message.error ? 'sm-flash-error' : 'sm-flash-success'}`}>
          {message.error ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {message.text}
          <button onClick={() => setMessage(null)}><X size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div className="sm-header">
        <div className="sm-header-left">
          <Share2 size={28} />
          <div>
            <h1>Redes Sociales</h1>
            <p className="sm-subtitle">Gestión de contenido & publicación multi-plataforma</p>
          </div>
        </div>
        <div className="sm-header-actions">
          <button className="sm-btn sm-btn-primary" onClick={() => openCompose()}>
            <Plus size={16} /> Nuevo Post
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="sm-stats">
        <div className="sm-stat">
          <span className="sm-stat-num">{activeAccounts}</span>
          <span className="sm-stat-label">Cuentas activas</span>
        </div>
        <div className="sm-stat">
          <span className="sm-stat-num">{totalPosts}</span>
          <span className="sm-stat-label">Posts totales</span>
        </div>
        <div className="sm-stat">
          <span className="sm-stat-num" style={{ color: '#f59e0b' }}>{scheduledPosts}</span>
          <span className="sm-stat-label">Programados</span>
        </div>
        <div className="sm-stat">
          <span className="sm-stat-num" style={{ color: '#4ade80' }}>{publishedPosts}</span>
          <span className="sm-stat-label">Publicados</span>
        </div>
        <div className="sm-stat">
          <span className="sm-stat-num" style={{ color: '#64748b' }}>{draftPosts}</span>
          <span className="sm-stat-label">Borradores</span>
        </div>
      </div>

      {/* Tab selector */}
      <div className="sm-view-toggle">
        <button className={tab === 'calendar' ? 'active' : ''} onClick={() => setTab('calendar')}>
          <Calendar size={16} /> Calendario
        </button>
        <button className={tab === 'compose' ? 'active' : ''} onClick={() => openCompose()}>
          <Edit3 size={16} /> Crear Post
        </button>
        <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
          <FileText size={16} /> Historial
        </button>
        <button className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>
          <Link2 size={16} /> Cuentas
        </button>
      </div>

      {/* ════════════════════════════════════════════════════
          TAB: ACCOUNTS
         ════════════════════════════════════════════════════ */}
      {tab === 'accounts' && (
        <div className="sm-accounts-section">
          <div className="sm-section-header">
            <h2>Cuentas Conectadas</h2>
            <button className="sm-btn sm-btn-primary sm-btn-sm" onClick={() => setShowAddAccount(true)}>
              <Plus size={14} /> Conectar Cuenta
            </button>
          </div>

          {/* Add account modal */}
          {showAddAccount && (
            <div className="sm-modal-overlay" onClick={() => setShowAddAccount(false)}>
              <div className="sm-modal" onClick={e => e.stopPropagation()}>
                <div className="sm-modal-header">
                  <h3>Conectar Red Social</h3>
                  <button onClick={() => setShowAddAccount(false)}><X size={18} /></button>
                </div>
                <div className="sm-modal-body">
                  <label>Plataforma</label>
                  <div className="sm-platform-picker">
                    {PLATFORMS.map(p => {
                      const Icon = p.icon
                      return (
                        <button
                          key={p.value}
                          className={`sm-platform-btn ${newAccountForm.platform === p.value ? 'active' : ''}`}
                          style={{ '--platform-color': p.color }}
                          onClick={() => setNewAccountForm(prev => ({ ...prev, platform: p.value }))}
                        >
                          <Icon size={20} />
                          <span>{p.label}</span>
                        </button>
                      )
                    })}
                  </div>
                  <label>Nombre de la cuenta</label>
                  <input
                    value={newAccountForm.name}
                    onChange={e => setNewAccountForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ej: GrowPro Argentina"
                  />
                  <label>Handle / usuario</label>
                  <input
                    value={newAccountForm.handle}
                    onChange={e => setNewAccountForm(prev => ({ ...prev, handle: e.target.value }))}
                    placeholder="@growpro.arg"
                  />
                  <label>Access Token</label>
                  <input
                    type="password"
                    value={newAccountForm.accessToken}
                    onChange={e => setNewAccountForm(prev => ({ ...prev, accessToken: e.target.value }))}
                    placeholder="Token de la API de la plataforma"
                  />
                  <p className="sm-hint">
                    El token permite publicar automáticamente. Sin token, las publicaciones quedarán como recordatorios.
                  </p>

                  {/* Platform-specific config */}
                  {(newAccountForm.platform === 'facebook') && (
                    <>
                      <label>Facebook Page ID</label>
                      <input
                        value={newAccountForm.pageId}
                        onChange={e => setNewAccountForm(prev => ({ ...prev, pageId: e.target.value }))}
                        placeholder="Ej: 123456789012345"
                      />
                      <p className="sm-hint">ID numérico de la Página de Facebook. Se encuentra en Configuración → Información de la página.</p>
                    </>
                  )}

                  {(newAccountForm.platform === 'instagram') && (
                    <>
                      <label>IG Business Account ID</label>
                      <input
                        value={newAccountForm.igBusinessAccountId}
                        onChange={e => setNewAccountForm(prev => ({ ...prev, igBusinessAccountId: e.target.value }))}
                        placeholder="Ej: 17841400000000000"
                      />
                      <p className="sm-hint">ID de la cuenta Business/Creator de Instagram vinculada a la página de Facebook.</p>
                      <label>Facebook Page ID (requerido para IG)</label>
                      <input
                        value={newAccountForm.pageId}
                        onChange={e => setNewAccountForm(prev => ({ ...prev, pageId: e.target.value }))}
                        placeholder="Page ID de la página de FB vinculada"
                      />
                    </>
                  )}

                  {(newAccountForm.platform === 'tiktok') && (
                    <>
                      <label>TikTok Open ID (opcional)</label>
                      <input
                        value={newAccountForm.tiktokOpenId}
                        onChange={e => setNewAccountForm(prev => ({ ...prev, tiktokOpenId: e.target.value }))}
                        placeholder="Open ID del usuario TikTok"
                      />
                      <p className="sm-hint">Se obtiene del flow OAuth de TikTok Developer.</p>
                    </>
                  )}
                </div>
                <div className="sm-modal-footer">
                  <button className="sm-btn sm-btn-secondary" onClick={() => setShowAddAccount(false)}>Cancelar</button>
                  <button className="sm-btn sm-btn-primary" onClick={handleAddAccount} disabled={processing}>
                    {processing ? <RefreshCw size={14} className="sm-spin" /> : <Link2 size={14} />}
                    Conectar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Account cards */}
          {accounts.length === 0 ? (
            <div className="sm-empty">
              <Share2 size={40} />
              <p>No hay cuentas conectadas</p>
              <span>Conectá tus redes sociales para comenzar a publicar</span>
            </div>
          ) : (
            <div className="sm-accounts-grid">
              {accounts.map(account => {
                const pInfo = getPlatformInfo(account.platform)
                const Icon = pInfo.icon
                return (
                  <div key={account.id} className={`sm-account-card ${account.status === 'paused' ? 'paused' : ''}`}>
                    <div className="sm-account-card-header" style={{ borderColor: pInfo.color }}>
                      <div className="sm-account-icon" style={{ background: pInfo.color }}>
                        <Icon size={20} color="white" />
                      </div>
                      <div className="sm-account-info">
                        <strong>{account.name}</strong>
                        <span>{account.handle}</span>
                      </div>
                      <div className={`sm-account-status ${account.status}`}>
                        {account.status === 'active' ? 'Activa' : 'Pausada'}
                      </div>
                    </div>
                    <div className="sm-account-card-body">
                      <span className="sm-account-platform">{pInfo.label}</span>
                      {account.hasToken ? (
                        <span className="sm-account-token-ok" title="Token configurado — publicación automática habilitada"><CheckCircle size={12} /> API OK</span>
                      ) : (
                        <span className="sm-account-token-missing" title="Sin token — solo recordatorios"><AlertTriangle size={12} /> Sin API</span>
                      )}
                      {account.connectedAt && (
                        <span className="sm-account-date">Conectada: {formatDate(account.connectedAt)}</span>
                      )}
                    </div>
                    <div className="sm-account-card-actions">
                      <button
                        className="sm-btn sm-btn-sm sm-btn-secondary"
                        onClick={() => handleToggleAccountStatus(account)}
                        title={account.status === 'active' ? 'Pausar' : 'Activar'}
                      >
                        {account.status === 'active' ? <Unlink size={14} /> : <Link2 size={14} />}
                        {account.status === 'active' ? 'Pausar' : 'Activar'}
                      </button>
                      <button
                        className="sm-btn sm-btn-sm sm-btn-danger"
                        onClick={() => handleDeleteAccount(account.id)}
                      >
                        <Trash2 size={14} /> Desconectar
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB: CALENDAR
         ════════════════════════════════════════════════════ */}
      {tab === 'calendar' && (
        <div className="sm-calendar-section">
          <div className="sm-calendar-container">
            <div className="sm-cal-nav">
              <button onClick={() => setCurrentDate(new Date(currentYear, currentMonth - 1, 1))}>
                <ChevronLeft size={18} />
              </button>
              <h2>{MONTH_NAMES[currentMonth]} {currentYear}</h2>
              <button onClick={() => setCurrentDate(new Date(currentYear, currentMonth + 1, 1))}>
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="sm-cal-grid">
              {DAY_NAMES.map(d => (
                <div key={d} className="sm-cal-day-header">{d}</div>
              ))}
              {calendarDays.map((day, i) => {
                if (day === null) return <div key={`empty-${i}`} className="sm-cal-cell empty" />
                const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                const dayPosts = postsByDate[dateStr] || []
                const isToday = new Date().toISOString().slice(0, 10) === dateStr
                const isSelected = selectedDate === dateStr
                return (
                  <div
                    key={dateStr}
                    className={`sm-cal-cell ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${dayPosts.length ? 'has-posts' : ''}`}
                    onClick={() => setSelectedDate(dateStr === selectedDate ? null : dateStr)}
                  >
                    <span className="sm-cal-day-num">{day}</span>
                    {dayPosts.length > 0 && (
                      <div className="sm-cal-day-posts">
                        {dayPosts.slice(0, 3).map(p => {
                          const statusInfo = getStatusInfo(p.status)
                          return (
                            <div
                              key={p.id}
                              className="sm-cal-post-dot"
                              title={`${p.content?.slice(0, 40)}... (${statusInfo.label})`}
                            >
                              <div className="sm-cal-post-platforms">
                                {(p.platforms || []).slice(0, 3).map(pl => {
                                  const plInfo = getPlatformInfo(pl)
                                  return <span key={pl} className="sm-cal-platform-dot" style={{ background: plInfo.color }} />
                                })}
                              </div>
                              <span className="sm-cal-post-status" style={{ color: statusInfo.color }}>
                                {statusInfo.label}
                              </span>
                            </div>
                          )
                        })}
                        {dayPosts.length > 3 && (
                          <span className="sm-cal-more">+{dayPosts.length - 3} más</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Selected date detail */}
          {selectedDate && (
            <div className="sm-cal-detail">
              <div className="sm-cal-detail-header">
                <h3>{new Date(selectedDate + 'T12:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
                <button className="sm-btn sm-btn-primary sm-btn-sm" onClick={() => openComposeForDate(selectedDate)}>
                  <Plus size={14} /> Programar post
                </button>
              </div>
              {(postsByDate[selectedDate] || []).length === 0 ? (
                <p className="sm-cal-detail-empty">No hay posts programados para esta fecha</p>
              ) : (
                <div className="sm-cal-detail-list">
                  {(postsByDate[selectedDate] || []).map(post => (
                    <div key={post.id} className="sm-post-card-mini">
                      <div className="sm-post-card-mini-left">
                        <div className="sm-post-platforms-row">
                          {(post.platforms || []).map(pl => {
                            const plInfo = getPlatformInfo(pl)
                            const Icon = plInfo.icon
                            return <Icon key={pl} size={14} style={{ color: plInfo.color }} />
                          })}
                        </div>
                        <span className="sm-post-preview">{post.content?.slice(0, 80) || post.caption?.slice(0, 80) || '(sin contenido)'}</span>
                        {post.scheduledAt && (
                          <span className="sm-post-time"><Clock size={12} /> {new Date(post.scheduledAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                        )}
                      </div>
                      <div className="sm-post-card-mini-actions">
                        <span className="sm-post-status-badge" style={{ color: getStatusInfo(post.status).color }}>
                          {getStatusInfo(post.status).label}
                        </span>
                        <button className="sm-btn sm-btn-sm sm-btn-secondary" onClick={() => openCompose(post)}>
                          <Edit3 size={12} />
                        </button>
                        {post.status === 'draft' || post.status === 'scheduled' ? (
                          <button className="sm-btn sm-btn-sm sm-btn-primary" onClick={() => handlePublishNow(post.id)}>
                            <Send size={12} />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB: COMPOSE
         ════════════════════════════════════════════════════ */}
      {tab === 'compose' && (
        <div className="sm-compose-section">
          <div className="sm-section-header">
            <h2>{editingPost ? 'Editar Post' : 'Crear Nuevo Post'}</h2>
            {editingPost && (
              <button className="sm-btn sm-btn-secondary sm-btn-sm" onClick={() => { resetCompose(); setTab('calendar') }}>
                <X size={14} /> Cancelar
              </button>
            )}
          </div>

          <div className="sm-compose-grid">
            {/* Left: form */}
            <div className="sm-compose-form">
              {/* Platform selection */}
              <div className="sm-compose-field">
                <label><Share2 size={14} /> Plataformas de destino</label>
                <div className="sm-compose-platforms">
                  {PLATFORMS.map(p => {
                    const Icon = p.icon
                    const connected = accounts.some(a => a.platform === p.value && a.status === 'active')
                    const selected = composeForm.platforms.includes(p.value)
                    return (
                      <button
                        key={p.value}
                        className={`sm-compose-platform-btn ${selected ? 'selected' : ''} ${!connected ? 'disconnected' : ''}`}
                        style={{ '--platform-color': p.color }}
                        onClick={() => togglePlatform(p.value)}
                        title={connected ? p.label : `${p.label} (no conectada)`}
                      >
                        <Icon size={18} />
                        <span>{p.label}</span>
                        {!connected && <span className="sm-compose-platform-warn">!</span>}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Content type */}
              <div className="sm-compose-field">
                <label>Tipo de contenido</label>
                <div className="sm-content-type-row">
                  {CONTENT_TYPES.map(ct => {
                    const Icon = ct.icon
                    return (
                      <button
                        key={ct.value}
                        className={`sm-content-type-btn ${composeForm.contentType === ct.value ? 'active' : ''}`}
                        onClick={() => setComposeForm(prev => ({ ...prev, contentType: ct.value }))}
                      >
                        <Icon size={14} /> {ct.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Content / Caption */}
              <div className="sm-compose-field">
                <label><FileText size={14} /> Contenido / Caption</label>
                <textarea
                  value={composeForm.content}
                  onChange={e => setComposeForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Escribí el contenido de tu publicación..."
                  rows={5}
                  maxLength={2200}
                />
                <span className="sm-char-count">{composeForm.content.length}/2200</span>
              </div>

              {/* Hashtags */}
              <div className="sm-compose-field">
                <label><Hash size={14} /> Hashtags</label>
                <input
                  value={composeForm.hashtags}
                  onChange={e => setComposeForm(prev => ({ ...prev, hashtags: e.target.value }))}
                  placeholder="#growpro #cultivo #indoor #cannabis"
                />
              </div>

              {/* Link */}
              {(composeForm.contentType === 'link' || composeForm.contentType === 'text') && (
                <div className="sm-compose-field">
                  <label><Link2 size={14} /> Link</label>
                  <input
                    value={composeForm.link}
                    onChange={e => setComposeForm(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              )}

              {/* Media URLs */}
              {(composeForm.contentType === 'image' || composeForm.contentType === 'video' || composeForm.contentType === 'carousel') && (
                <div className="sm-compose-field">
                  <label><Image size={14} /> URLs de medios (una por línea)</label>
                  <textarea
                    value={composeForm.mediaUrls.join('\n')}
                    onChange={e => setComposeForm(prev => ({ ...prev, mediaUrls: e.target.value.split('\n').filter(u => u.trim()) }))}
                    placeholder="https://ejemplo.com/imagen1.jpg&#10;https://ejemplo.com/imagen2.jpg"
                    rows={3}
                  />
                  <p className="sm-hint">Podés pegar URLs de imágenes o videos. En el futuro se habilitará subir archivos directamente.</p>
                </div>
              )}

              {/* Schedule */}
              <div className="sm-compose-field">
                <label><Clock size={14} /> Programar publicación</label>
                <input
                  type="datetime-local"
                  value={composeForm.scheduledAt}
                  onChange={e => setComposeForm(prev => ({ ...prev, scheduledAt: e.target.value }))}
                />
                <p className="sm-hint">Dejá vacío para guardar como borrador sin fecha.</p>
              </div>

              {/* Notes */}
              <div className="sm-compose-field">
                <label>Notas internas</label>
                <input
                  value={composeForm.notes}
                  onChange={e => setComposeForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notas para el equipo (no se publican)"
                />
              </div>
            </div>

            {/* Right: preview */}
            <div className="sm-compose-preview">
              <h3><Eye size={16} /> Vista Previa</h3>
              <div className="sm-preview-card">
                <div className="sm-preview-platforms">
                  {composeForm.platforms.map(pl => {
                    const plInfo = getPlatformInfo(pl)
                    const Icon = plInfo.icon
                    return <span key={pl} className="sm-preview-platform" style={{ color: plInfo.color }}><Icon size={14} /> {plInfo.label}</span>
                  })}
                  {composeForm.platforms.length === 0 && <span className="sm-preview-empty">Seleccioná plataformas</span>}
                </div>

                {composeForm.mediaUrls.length > 0 && (
                  <div className="sm-preview-media">
                    {composeForm.mediaUrls.slice(0, 4).map((url, i) => (
                      <div key={i} className="sm-preview-media-item">
                        <Image size={24} />
                        <span>{url.split('/').pop() || `Media ${i + 1}`}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="sm-preview-content">
                  {composeForm.content || <span className="sm-preview-empty">Tu contenido aparecerá aquí...</span>}
                </div>

                {composeForm.hashtags && (
                  <div className="sm-preview-hashtags">{composeForm.hashtags}</div>
                )}

                {composeForm.link && (
                  <div className="sm-preview-link"><Link2 size={12} /> {composeForm.link}</div>
                )}

                {composeForm.scheduledAt && (
                  <div className="sm-preview-schedule">
                    <Clock size={12} /> Programado: {formatDateTime(composeForm.scheduledAt)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="sm-compose-actions">
            <button className="sm-btn sm-btn-secondary" onClick={() => handleSavePost(true)} disabled={processing}>
              <Save size={16} /> Guardar borrador
            </button>
            <button className="sm-btn sm-btn-primary" onClick={() => handleSavePost(false)} disabled={processing}>
              {processing ? <RefreshCw size={16} className="sm-spin" /> : <Calendar size={16} />}
              {composeForm.scheduledAt ? 'Programar' : 'Guardar'}
            </button>
            {editingPost && (composeForm.status === 'draft' || composeForm.status === 'scheduled') && (
              <button className="sm-btn sm-btn-success" onClick={() => handlePublishNow(editingPost)} disabled={processing}>
                <Send size={16} /> Publicar Ahora
              </button>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          TAB: HISTORY
         ════════════════════════════════════════════════════ */}
      {tab === 'history' && (
        <div className="sm-history-section">
          <div className="sm-section-header">
            <h2>Historial de Posts</h2>
          </div>

          {/* Toolbar */}
          <div className="sm-toolbar">
            <div className="sm-search">
              <Search size={16} />
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Buscar posts..."
              />
            </div>
            <div className="sm-filters">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="all">Todos los estados</option>
                {POST_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)}>
                <option value="all">Todas las redes</option>
                {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          {/* Posts list */}
          {filteredPosts.length === 0 ? (
            <div className="sm-empty">
              <FileText size={40} />
              <p>No hay posts{statusFilter !== 'all' || searchTerm ? ' que coincidan' : ''}</p>
            </div>
          ) : (
            <div className="sm-post-list">
              {filteredPosts.map(post => {
                const statusInfo = getStatusInfo(post.status)
                return (
                  <div key={post.id} className="sm-post-card">
                    <div className="sm-post-card-top">
                      <div className="sm-post-platforms-row">
                        {(post.platforms || []).map(pl => {
                          const plInfo = getPlatformInfo(pl)
                          const Icon = plInfo.icon
                          return <Icon key={pl} size={16} style={{ color: plInfo.color }} title={plInfo.label} />
                        })}
                      </div>
                      <span className="sm-post-status-badge" style={{ background: statusInfo.color + '22', color: statusInfo.color, borderColor: statusInfo.color + '44' }}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <div className="sm-post-card-content">
                      <p>{post.content?.slice(0, 140) || post.caption?.slice(0, 140) || '(sin contenido)'}</p>
                      {post.hashtags && <span className="sm-post-hashtags">{post.hashtags}</span>}
                    </div>
                    <div className="sm-post-card-meta">
                      {post.scheduledAt && (
                        <span><Clock size={12} /> {formatDateTime(post.scheduledAt)}</span>
                      )}
                      {post.createdBy && <span>por {post.createdBy}</span>}
                      <span>{formatDate(post.createdAt)}</span>
                    </div>
                    <div className="sm-post-card-actions">
                      <button className="sm-btn sm-btn-sm sm-btn-secondary" onClick={() => openCompose(post)}>
                        <Edit3 size={12} /> Editar
                      </button>
                      {(post.status === 'draft' || post.status === 'scheduled') && (
                        <button className="sm-btn sm-btn-sm sm-btn-primary" onClick={() => handlePublishNow(post.id)}>
                          <Send size={12} /> Publicar
                        </button>
                      )}
                      <button className="sm-btn sm-btn-sm sm-btn-danger" onClick={() => handleDeletePost(post.id)}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
